import React from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";

const LEVEL_TITLES = {
  1.1: "Graduate Trainee",
  1.2: "Graduate Trainee",
  2.1: "Software Eng.",
  2.2: "Sr. Software Eng.",
  3.1: "Tech Lead",
  3.2: "Sr. Tech Lead",
  3.3: "Principal Lead",
  4.1: "Principal Engineer",
};

const Step1Position = ({ positions, selected, onSelect, loading }) => {
  const theme = useTheme();
  const t = theme.custom;

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: "40px" }}>
        <CircularProgress size={20} sx={{ color: t.accent }} />
      </Box>
    );

  if (!positions.length)
    return (
      <Box sx={{ py: "20px" }}>
        <Typography
          sx={{
            fontFamily: "'DM Mono', monospace",
            fontSize: "11px",
            color: t.text3,
          }}
        >
          No open positions available for your MAC
        </Typography>
      </Box>
    );

  // Group positions by sowName
  const grouped = positions.reduce((acc, p) => {
    const key = p.sowName || "Unknown SOW";
    if (!acc[key]) acc[key] = [];
    acc[key].push(p);
    return acc;
  }, {});

  const sowGroups = Object.entries(grouped); // [["Enterprise Data", [...]], ...]

  return (
    <Box>
      <Typography
        sx={{
          fontFamily: "'DM Mono', monospace",
          fontSize: "8px",
          letterSpacing: "0.15em",
          textTransform: "uppercase",
          color: t.text3,
          mb: "14px",
        }}
      >
        Select Position <span style={{ color: t.accent }}>*</span>
      </Typography>

      {sowGroups.map(([sowName, sowPositions]) => (
        <Box key={sowName} sx={{ mb: "18px" }}>
          {/* SOW section header */}
<Box
  sx={{
    display: "flex",
    alignItems: "center",
    gap: "8px",
    mb: "8px",
  }}
>
  <Typography
    sx={{
      fontFamily: "'DM Mono', monospace",
      fontSize: "8px",
      letterSpacing: "0.16em",
      textTransform: "uppercase",
      color: t.text3,
    }}
  >
    SOW
  </Typography>
  <Box
    sx={{
      background: t.surface3,
      borderRadius: "3px",
      padding: "2px 8px",
    }}
  >
    <Typography
      sx={{
        fontFamily: "'DM Mono', monospace",
        fontSize: "9px",
        fontWeight: 600,
        letterSpacing: "0.08em",
        color: t.text2,
      }}
    >
      {sowName}
    </Typography>
  </Box>
  <Typography
    sx={{
      fontFamily: "'DM Mono', monospace",
      fontSize: "8px",
      color: t.text3,
    }}
  >
    ·
  </Typography>
  <Box
    sx={{
      background: t.surface3,
      borderRadius: "3px",
      padding: "2px 8px",
    }}
  >
    <Typography
      sx={{
        fontFamily: "'DM Mono', monospace",
        fontSize: "9px",
        fontWeight: 600,
        letterSpacing: "0.08em",
        color: t.text2,
      }}
    >
      {sowPositions[0]?.accountName}
    </Typography>
  </Box>
  <Box
    sx={{
      flex: 1,
      height: "1px",
      background: t.border,
    }}
  />
  <Typography
    sx={{
      fontFamily: "'DM Mono', monospace",
      fontSize: "8px",
      color: t.text3,
    }}
  >
    {sowPositions.length} open
  </Typography>
</Box>

          {/* Positions under this SOW */}
          {sowPositions.map((p) => {
            const isSelected = selected?.positionId === p.positionId;
            const levelTitle = LEVEL_TITLES[p.levelCode] || "";

            return (
              <Box
                key={p.positionId}
                onClick={() => onSelect?.(p)}
                sx={{
                  border: isSelected
                    ? `1px solid ${t.accent}88`
                    : `1px solid ${t.border2}`,
                  background: isSelected ? t.accent2 : t.surface2,
                  borderRadius: "3px",
                  padding: "10px 14px",
                  mb: "6px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: "12px",
                  transition: "all 0.15s",
                  "&:hover": { borderColor: t.accent + "55" },
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontFamily: "'DM Mono', monospace",
                      fontSize: "10px",
                      color: t.accent,
                      fontWeight: 500,
                      mb: "2px",
                    }}
                  >
                    {p.positionCode}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "'Inter', sans-serif",
                      fontSize: "13px",
                      fontWeight: 600,
                      color: t.text,
                    }}
                  >
                    {p.roleTitle}
                  </Typography>
                  <Typography
                    sx={{ fontSize: "11px", color: t.text3, mt: "2px" }}
                  >
                    Level {p.levelCode}
                    {levelTitle ? ` — ${levelTitle}` : ""}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "4px", flexShrink: 0 }}>
                  <Box
                    sx={{
                      background: p.positionType === "BIL" ? t.accent2 : t.gold2,
                      border: `1px solid ${p.positionType === "BIL" ? t.accent + "44" : t.gold + "44"}`,
                      borderRadius: "2px",
                      padding: "2px 8px",
                      mt: "2px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "'DM Mono', monospace",
                        fontSize: "8px",
                        color: p.positionType === "BIL" ? t.accent : t.gold,
                        letterSpacing: "0.1em",
                        fontWeight: 600,
                      }}
                    >
                      {p.positionType === "BIL" ? "BILLED" : "UNBILLED"}
                    </Typography>
                  </Box>
                  {p.billedFte != null && (
                    <Box sx={{
                      background: p.positionType === "BIL" ? t.accent2 : t.gold2,
                      border: `1px solid ${p.positionType === "BIL" ? t.accent + "44" : t.gold + "44"}`,
                      borderRadius: "2px", padding: "2px 8px",
                      display: "flex", alignItems: "center", gap: "5px",
                    }}>
                      <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", color: t.text3, letterSpacing: "0.08em" }}>
                        BILLED FTE
                      </Typography>
                      <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", fontWeight: 700, color: p.positionType === "BIL" ? t.accent : t.gold }}>
                        {p.billedFte}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
};

export default Step1Position;
